import { connect } from 'react-redux'
import Component from '../component'
import {
  setFreeroundsBet, setFreeroundsLeft, message as msg, makeFreeRoundChoice,
  displayFreeround
} from '../../redux/actions'
import Currency from '../../Utils/Currency'
import { MESSAGE_TYPES } from '../../redux/constants'
import { timeUntilExpiration } from '../../Utils'

const tournamentPoints = 'tournamentPoints'

/**
 * The Free Round message is displayed when the player has received vouchers,
 * or something similar. On login it will display how many you have, or if you
 * have a choice to make (freespin v2). It will also notify you when the free
 * rounds are over.
 */
@connect(state => ({ currencyChanged: state.base.currencyChanged }), { setFreeroundsBet, setFreeroundsLeft, msg, makeFreeRoundChoice, displayFreeround })
export default class FreeRoundMessage extends Component {
  componentDidMount() {
    window.addEventListener('resize', this.onResize)
    this.onResize()
    this.forceUpdate()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.currencyChanged !== this.props.currencyChanged) {
      this.forceUpdate()
    }
  }

  onResize = () => {
    const { innerWidth, innerHeight } = window
    const { offsetWidth = 0, offsetHeight = 0 } = this.wrapper
    const isPortrait = innerWidth < innerHeight
    const popupPadding = isPortrait ? 20 : 0

    const scale = Math.min(Math.min(innerWidth / (offsetWidth + popupPadding), innerHeight / offsetHeight), 2)

    this.wrapper.style.transform = `scale(${scale}) translate(-50%, -50%)`
    this.wrapper.style.transformOrigin = '0 0'
  }

  /**
   * Create the end message, which is shown when the free rounds are over.
   * @param {object} data - Object with information about freerounds / tournament spins.
   * @returns {*} - DOM element.
   */
  getEndMessage = (data) => {
    const { win, type = '' } = data
    const isTournament = type === tournamentPoints
    const ret = []
    if (win > 0) {
      ret.push(this.t(`${isTournament ? 'tournament' : 'freerounds'}.end`, { amount: Currency.format(win, { showCurrency: !isTournament, useSeparators: false }) }))
      ret.push(<br />)
      ret.push(this.t('freerounds.congrats'))
    }
    else {
      ret.push(this.t('freerounds.nowin'))
    }

    if (!isTournament) {
      ret.push(<br />)
      ret.push(this.t('freerounds.nowreal'))
    }

    return (
      <div className="freeroundMessage endScreen">
        { ret }
      </div>
    )
  }

  /**
   * Display message on when the game is launched.
   * @param {number} spins - How many spins the user has left.
   * @param {number} ba - The bet amount in which the free rounds are played.
   * @param {boolean} isTournament - If the spins are of tournament type.
   * @returns {*} - The DOM component.
   */
  getStartMessage = (spins, ba, isTournament, featureTrigger) => {
    let message = {}

    if (spins > 0) {
      const msg = `${isTournament ? 'tournament' : (featureTrigger ? 'featureTrigger' : 'freerounds')}.start`
      const val = isTournament ? { amount: spins } : { spins, amount: Currency.format(ba, { showCurrency: !isTournament }) }
      message = this.t(msg, val)
    } else {
      message = this.t(`${isTournament ? 'tournament' : 'freerounds'}.final`)
    }

    return (
      <div className="freeroundMessage">
        <div className="introMessage" key="intro">
          <div className="title">{this.t('freerounds.congrats')}</div>
          <div className="divider" />
        </div>
        <div className="choices centered">
          { message }
          { this.t('freerounds.goodluck') }
        </div>
        <br />
      </div>
    )
  }

  /**
   * Creates a list of choices for the user, out of the choices provided.
   * @param {array} betSizeOptions - Array of different options, containing amount of spins and bet size.
   * @returns {Array} - Array of option DOM elements.
   */
  getOptions = (betSizeOptions) => {
    const options = []
    betSizeOptions.forEach((o, i) => {
      const spins = o.spins
      const choiceElement = this.createSpinChoice(i, spins, o.ba, o.l)
      options.push(choiceElement)
    })
    return options
  }

  /**
   * Creates a button element for different spin choices.
   * @param {number} i - Iterator index used for element key.
   * @param {number} spins - Amount of spins.
   * @param {number} betAmount - Bet amount for the choice (in cents).
   * @param {number} betLines - Number of bet lines.
   * @returns {*}
   */
  createSpinChoice(i, spins, betAmount, betLines) {
    return (
      <div key={i} className="freeRoundChoice">
        <button key="spin" onClick={e => { this.closeMessage(e, spins, betAmount, betLines) }}>
          {this.t('freeround.spins', { number: spins })}
        </button>
        <div className="amountPerRound">{this.t('freeround.perSpin', { amount: Currency.format(betAmount, { showCurrency: true }) })}</div>
      </div>
    )
  }

  /**
   * Main method that chooses which type of message to create.
   * @param {object} freeRounds - Information about the free rounds.
   * @returns {*} - Returns appropriate DOM element to display.
   */
  getMessage = (freeRounds) => {
    const { spins, ba, spinsended, betSizeOptions, type = '', featureTrigger, expires } = freeRounds
    const isTournament = type === tournamentPoints

    if (spinsended || timeUntilExpiration(expires) <= 0)
      return this.getEndMessage(freeRounds)

    if (betSizeOptions)
      return this.getOptions(betSizeOptions)

    return this.getStartMessage(spins, ba, isTournament, featureTrigger)
  }

  /**
   * The close function for the message.
   * Will update the UI to set the right bet amount
   * and spins left.
   * @param {object} e - Event from clicking the button.
   * @param {number} spins - Amount of spins chosen.
   * @param {number} bet - The bet amount for each spin.
   * @param {number} betLines - The betLines amount for each spin.
   * @param {boolean} spinsended - Whether or not the spins have ended.
   */
  closeMessage = (e, spins, bet, betLines, spinsended) => {
    if (!spinsended) {
      this.props.setFreeroundsBet(bet, betLines)
      this.props.setFreeroundsLeft(spins)
    } else {
      this.props.displayFreeround(false)
    }

    this.props.makeFreeRoundChoice()

    e.preventDefault()
  }

  /**
   * Calls an action to send a skip / delete choice to the server.
   * @param {string} choice - Either "skip" or "delete".
   */
  makeChoice = (choice) => {
    if (!choice) {
      return
    }

    this.props.displayFreeround(false)
    this.props.makeFreeRoundChoice(choice)
  }

  /**
   * Create and return a set of actions for specific scenario.
   * @param {object} props - Current props
   * @returns {Array} - Returns an array of DOM elements.
   */
  getActionButtons(freeRounds) {
    const { betSizeOptions, spinsPlayed, spinsended, type = '', imsFreespins, expires } = freeRounds
    const hasExpired = timeUntilExpiration(expires) <= 0
    const spinsInProgress = spinsPlayed > 0 && (!spinsended && !hasExpired)
    const isTournament = type === tournamentPoints

    const actions = []
    if ((spinsended || hasExpired) || (spinsInProgress && !betSizeOptions) || isTournament) {
      actions.push(
        <button key="ok" onClick={e => this.closeMessage(e, freeRounds.spins, freeRounds.ba, freeRounds.l, (spinsended || hasExpired))}>
          {this.t('realityCheck.ok')}
        </button>
      )
    } else {
      if (!betSizeOptions) {
        actions.push(
          <button key="ok" className="full" onClick={e => this.closeMessage(e, freeRounds.spins, freeRounds.ba, freeRounds.l)}>
            {this.t('realityCheck.ok')}
          </button>
        )
      }
      actions.push(
        <button key="skip" onClick={() => { this.makeChoice('skip') }}>
          {this.t('freeround.skip')}
        </button>
      )
      if (!imsFreespins) {
        actions.push(
          <button key="delete" onClick={() => { this.makeChoice('delete') }}>
            {this.t('freeround.delete')}
          </button>
        )
      }
    }

    return actions
  }

  /**
   * Create message content which is to be displayed above the actions.
   * @param {object} freeRounds - Information about the current free rounds.
   * @returns {*}
   */
  getContent(freeRounds) {
    const { betSizeOptions } = freeRounds

    if (betSizeOptions) {
      const content = []
      content.push(
        <div className="introMessage" key="intro">
          <div className="title">{this.t('freerounds.congrats')}</div>
          <div className="divider" />
          <div className="content">{this.t('freeround.introText')}</div>
          <div className="content">{this.t('freeround.introText2')}</div>
        </div>
      )
      content.push(
        <div className="choices" key="choices">
          { this.getMessage(freeRounds) }
        </div>
      )
      return content
    }

    return this.getMessage(freeRounds)
  }

  /**
   * Render method will render out the current free round message as a DOM element.
   * @returns {*}
   */
  render() {
    const { message: { data = {} } } = this.props
    const parsedData = { ...data,
      ba: data.l ? data.l * data.la : data.ba,
      betSizeOptions: data.betSizeOptions && data.betSizeOptions.map(o => ({ ...o, ba: o.l ? o.l * o.la : o.ba }))
    }
    const { betSizeOptions, spinsPlayed, spinsended, type = '' } = parsedData
    const spinsInProgress = spinsPlayed > 0 && !spinsended
    const contentMessage = this.getContent(parsedData)
    const actionButtons = this.getActionButtons(parsedData)
    const isTournament = type === tournamentPoints

    return (
      <div className={`popup type-${MESSAGE_TYPES.FREEROUNDS}`} ref={p => { this.wrapper = p }}>
        <div className="normal freeround">
          { isTournament || (!spinsended && !spinsInProgress) && <div className="closeButton fa fa-close" onClick={() => { this.makeChoice('skip') }} /> }
          { contentMessage }
          { betSizeOptions && (
            <div className="total">{this.t('freeround.totalValue', { amount: Currency.format(betSizeOptions[0].ba * betSizeOptions[0].spins) })}</div>
          )}
          <div className={`buttonContainer ${actionButtons.length === 2 ? 'two' : 'one'}-col`}>
            { actionButtons }
          </div>
        </div>
      </div>
    )
  }
}
